import { t } from '../i18n';
import { useEffect, useState } from 'react';
import { documentId, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { getOfferingKey, surveyResponsesCollection } from '../utils/courseIdentity';
import { firestoreCache, CACHE_KEYS, CACHE_TTL } from '../utils/firestoreCache';
import { SurveyResponse } from '../types/surveyResponse';

export type CourseSurveyResponse = SurveyResponse & { id: string };

type UseCourseSurveyResponsesResult = {
  responses: CourseSurveyResponse[];
  loading: boolean;
  error: string | null;
};

export const useCourseSurveyResponses = (courseId: string | undefined, academicYear?: string): UseCourseSurveyResponsesResult => {
  const [responses, setResponses] = useState<CourseSurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!courseId || !academicYear) { setResponses([]); setLoading(false); setError(null); return; }
      const cacheKey = CACHE_KEYS.courseSurveyResponses(getOfferingKey({ id: courseId, academicYear }), academicYear);
      const cached = firestoreCache.get<CourseSurveyResponse[]>(cacheKey);
      if (cached) { setResponses(cached); setLoading(false); setError(null); return; }
      setLoading(true); setError(null);
      try {
        const snapshot = await getDocs(query(surveyResponsesCollection(db, { id: courseId, academicYear }), orderBy(documentId())));
        const next = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as SurveyResponse) }));
        firestoreCache.set(cacheKey, next, CACHE_TTL.COURSE_SURVEY_RESPONSES);
        if (!cancelled) setResponses(next);
      } catch (err) {
        console.error('[useCourseSurveyResponses] load failed:', err);
        if (!cancelled) { setResponses([]); setError(t("受講者コメントの取得に失敗しました")); }
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [courseId, academicYear]);

  return { responses, loading, error };
};
